import { CubePresenter, Logger, NOOP_VOID } from 'wdc-cube';
import { passwordProblem } from '../../../../domain';
import { AccountApi } from '../account.api';
import { SignInKeys } from '../sign-in/auth.key';
import { ForgotPasswordKeys } from './forgot-password.key';
import { ForgotPasswordScope } from './forgot-password.scope';
const LOG = Logger.get('Mormaii.ForgotPasswordPresenter');
// @Inject
const accountApi = AccountApi.INSTANCE;
/**
 * Quanto vale o prazo do codigo, em segundos.
 *
 * O backend expira em tres minutos (`PasswordResetService.expiration`). O painel
 * antigo conta 180 na tela; contar o mesmo aqui mantem o "Reenviar" liberando no
 * momento em que o codigo de fato morreu.
 */
const CODE_LIFETIME = 180;
/**
 * "Esqueci minha senha", em tres etapas.
 *
 * O e-mail e o token temporario ficam NESTE presenter e em lugar nenhum mais: nao
 * vao para o endereco, nao vao para o armazenamento e nao viram sessao. O token
 * serve para uma unica chamada, a que grava a senha nova.
 */
export class ForgotPasswordPresenter extends CubePresenter {
    constructor(app) {
        super(app, new ForgotPasswordScope());
        this.parentSlot = NOOP_VOID;
    }
    release() {
        this.stopClock();
        super.release();
        LOG.info('Finalized');
    }
    async applyParameters(intent, initialization) {
        const keys = new ForgotPasswordKeys(this.app, intent);
        if (initialization) {
            this.scope.onEmailChanged = this.handleChange.bind(this, 'email');
            this.scope.onCodeChanged = this.handleCodeChanged.bind(this);
            this.scope.onPasswordChanged = this.handleChange.bind(this, 'password');
            this.scope.onConfirmPasswordChanged = this.handleChange.bind(this, 'confirmPassword');
            this.scope.onSubmit = this.action(this.onSubmit);
            this.scope.onResend = this.action(this.onResend);
            this.scope.onBack = this.action(this.onBack);
            this.scope.onGoToSignIn = this.action(this.onGoToSignIn);
            LOG.info('Initialized');
        }
        // O slot e decidido acima na composicao e pode mudar entre viagens: le-se sempre.
        this.parentSlot = keys.parentSlot;
        this.parentSlot(this.scope);
        return true;
    }
    // Nao sao acoes: so espelham o que foi digitado. Redesenhar o campo a cada
    // tecla e como um campo perde o cursor.
    handleChange(field, value) {
        this.scope[field] = value;
    }
    /** O codigo do e-mail e sempre em MAIUSCULA, como no painel antigo. */
    handleCodeChanged(value) {
        this.scope.code = value.toUpperCase();
    }
    // ========== AS TRES ETAPAS ==========
    async onSubmit() {
        switch (this.scope.step) {
            case 'email':
                await this.askForCode();
                return;
            case 'code':
                await this.exchangeCode();
                return;
            case 'password':
                await this.saveNewPassword();
        }
    }
    async askForCode() {
        const email = this.scope.email.trim();
        if (!email || !email.includes('@')) {
            this.scope.error = 'Informe um e-mail válido.';
            return;
        }
        await this.whileBusy(async () => {
            await accountApi.requestPasswordReset(email);
            this.scope.email = email;
            this.scope.step = 'code';
            this.startClock();
        });
    }
    async exchangeCode() {
        const code = this.scope.code.trim();
        if (code.length !== 5) {
            this.scope.error = 'O código tem 5 caracteres.';
            return;
        }
        await this.whileBusy(async () => {
            this.resetToken = await accountApi.confirmResetCode(this.scope.email, code);
            this.stopClock();
            this.scope.step = 'password';
        });
    }
    async saveNewPassword() {
        const password = this.scope.password;
        const problem = passwordProblem(password);
        if (problem) {
            this.scope.error = problem;
            return;
        }
        if (password !== this.scope.confirmPassword) {
            this.scope.error = 'As senhas devem ser iguais.';
            return;
        }
        if (!this.resetToken) {
            // So acontece se alguem chegar aqui sem passar pela etapa do codigo.
            this.scope.error = 'O código expirou. Comece de novo.';
            this.scope.step = 'email';
            return;
        }
        await this.whileBusy(async () => {
            await accountApi.confirmNewPassword(this.scope.email, password, this.resetToken);
            // O token temporario morre aqui: ele servia para esta chamada e nada mais.
            this.resetToken = undefined;
            this.scope.password = '';
            this.scope.confirmPassword = '';
            this.scope.done = true;
        });
    }
    async onResend() {
        if (this.scope.secondsLeft > 0) {
            return;
        }
        await this.whileBusy(async () => {
            await accountApi.requestPasswordReset(this.scope.email);
            this.scope.code = '';
            this.startClock();
        });
    }
    /**
     * Voltar: uma etapa atras, ou para a porta quando ja se esta na primeira.
     *
     * O token e a senha digitada sao descartados no caminho - voltar para pedir
     * outro codigo nao deve carregar o anterior.
     */
    async onBack() {
        this.scope.error = undefined;
        if (this.scope.step === 'password') {
            this.resetToken = undefined;
            this.scope.password = '';
            this.scope.confirmPassword = '';
            this.scope.step = 'email';
            this.scope.code = '';
            this.stopClock();
            return;
        }
        if (this.scope.step === 'code') {
            this.scope.step = 'email';
            this.scope.code = '';
            this.stopClock();
            return;
        }
        await this.onGoToSignIn();
    }
    async onGoToSignIn() {
        this.stopClock();
        await SignInKeys.of(this.app).flip();
    }
    // ========== O RELOGIO ==========
    startClock() {
        this.stopClock();
        this.scope.secondsLeft = CODE_LIFETIME;
        this.scope.timer = formatClock(CODE_LIFETIME);
        this.ticking = setInterval(() => {
            const left = Math.max(0, this.scope.secondsLeft - 1);
            this.scope.secondsLeft = left;
            this.scope.timer = formatClock(left);
            if (left === 0) {
                this.stopClock();
            }
        }, 1000);
    }
    stopClock() {
        if (this.ticking) {
            clearInterval(this.ticking);
            this.ticking = undefined;
        }
    }
    /** O `busy` e o `error` sao iguais nas tres etapas; so o meio muda. */
    async whileBusy(work) {
        this.scope.busy = true;
        this.scope.error = undefined;
        try {
            await work();
        }
        catch (caught) {
            this.scope.error = caught instanceof Error ? caught.message : 'Não foi possível continuar.';
        }
        finally {
            this.scope.busy = false;
        }
    }
}
function formatClock(seconds) {
    const minutes = Math.floor(seconds / 60);
    const rest = seconds % 60;
    return `${minutes}:${rest < 10 ? `0${rest}` : rest}`;
}
//# sourceMappingURL=forgot-password.presenter.js.map